import { tsv } from "@faire/slate/lib/tsv";

import { cn } from "@faire/prototype-playground/lib/utils";

import { usePlaygroundImageWithFallback } from "../../hooks/usePlaygroundImageWithFallback";

import AskAssistantPill from "./AskAssistantPill";

export interface BrandStorefrontHeaderData {
  name: string;
  avatarUrl: string;
  coverUrl: string;
  rating: number;
  reviewCount: number;
  location: string;
  minOrder: string;
  shipBy: string;
  topShop?: boolean;
}

export interface BrandStorefrontHeaderProps {
  brand: BrandStorefrontHeaderData;
  isFollowing?: boolean;
  onFollow?: () => void;
  /** When provided, renders the "Ask your assistant" pill alongside Follow. */
  onAskAssistant?: () => void;
  /** Called when the messenger control is pressed. */
  onMessage?: () => void;
  /**
   * How the messenger control is presented. `icon` is the bare circular chat
   * glyph used in production; `labelled` swaps it for a "Message brand" button.
   */
  messageStyle?: "icon" | "labelled";
  /** Overrides the messenger glyph. Defaults to the built-in chat bubble. */
  MessageIcon?: React.FC<{ width?: string; height?: string }>;
  /**
   * Optional line under the action row, e.g. "Usually responds in a few hours".
   * Omitting it leaves the header's markup exactly as it was.
   */
  messageSubtext?: string;
  className?: string;
}

/**
 * Brand storefront header: full-bleed cover image, overlapping brand avatar,
 * name, Top Shop badge, rating · location · minimum, ship-by estimate, and the
 * Follow / Message / more-actions controls.
 */
export default function BrandStorefrontHeader({
  brand,
  isFollowing = false,
  onFollow,
  onAskAssistant,
  onMessage,
  messageStyle = "icon",
  MessageIcon,
  messageSubtext,
  className,
}: BrandStorefrontHeaderProps) {
  const MessageGlyph = MessageIcon ?? DefaultMessageIcon;
  const cover = usePlaygroundImageWithFallback(brand.coverUrl);
  const avatar = usePlaygroundImageWithFallback(brand.avatarUrl);

  const messageControl =
    messageStyle === "labelled" ? (
      // Same outline treatment as Follow: a labelled peer action rather
      // than a filled button competing with the page's primary CTAs.
      <button
        type="button"
        onClick={onMessage}
        className={cn(actionPillStyle.cls(), "flex items-center gap-2")}
      >
        <MessageGlyph />
        Message brand
      </button>
    ) : (
      <IconCircleButton ariaLabel="Message" onClick={onMessage}>
        <MessageGlyph />
      </IconCircleButton>
    );

  return (
    <div className={cn("w-full", className)}>
      {/* Cover */}
      <div className="bg-fs-border-muted aspect-[4.4] max-h-[240px] min-h-[120px] w-full overflow-hidden lg:min-h-[240px]">
        <img
          src={cover.src}
          onError={cover.onError}
          alt={brand.name}
          referrerPolicy="no-referrer"
          className="h-full w-full object-cover object-center brightness-95"
        />
      </div>

      {/* Identity + actions */}
      <div className="flex w-full flex-row justify-between gap-4 px-4 pt-6 pb-6 md:px-10 lg:px-12">
        <div className="flex flex-row">
          <img
            src={avatar.src}
            onError={avatar.onError}
            alt={brand.name}
            referrerPolicy="no-referrer"
            className="h-24 w-24 shrink-0 rounded-full object-cover"
          />
          <div className="ml-4 flex flex-col justify-center">
            <h1 className="type-fs-section-heading text-fs-text-primary">
              {brand.name}
            </h1>

            {/* Meta row */}
            <div className="mt-1 flex flex-row flex-wrap items-center gap-y-1">
              {brand.topShop ? (
                <>
                  <span className="flex flex-row items-center gap-1">
                    <ShieldIcon />
                    <span className="type-fs-paragraph-medium text-fs-text-primary">
                      Top Shop
                    </span>
                  </span>
                  <MetaDivider />
                </>
              ) : null}

              <span className="flex flex-row items-center gap-1">
                <StarIcon />
                <span className="type-fs-paragraph text-fs-text-primary">
                  {brand.rating.toFixed(1)}
                </span>
                <span className="type-fs-paragraph text-fs-text-primary underline">
                  ({brand.reviewCount})
                </span>
              </span>
              <MetaDivider />

              <span className="type-fs-paragraph text-fs-text-primary">
                {brand.location}
              </span>
              <MetaDivider />

              <span className="type-fs-paragraph text-fs-text-primary">
                {brand.minOrder}
              </span>
            </div>

            {/* Shipping row */}
            <div className="mt-1 flex flex-row flex-wrap items-center gap-y-1">
              <span className="type-fs-paragraph text-fs-text-primary">
                {brand.shipBy}
              </span>
              <MetaDivider />
              <button
                type="button"
                className="type-fs-paragraph text-fs-text-primary underline"
              >
                Shipping details
              </button>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex shrink-0 flex-row items-start gap-2">
          {onAskAssistant ? (
            <AskAssistantPill onClick={() => onAskAssistant()} />
          ) : null}
          <button
            type="button"
            onClick={onFollow}
            className={actionPillStyle.cls()}
          >
            {isFollowing ? "Following" : "Follow"}
          </button>
          {messageSubtext ? (
            <div className="flex flex-col items-center gap-3">
              {messageControl}
              <p className="type-fs-label text-fs-text-subdued whitespace-nowrap">
                {messageSubtext}
              </p>
            </div>
          ) : (
            messageControl
          )}
          <IconCircleButton ariaLabel="More actions">
            <MoreIcon />
          </IconCircleButton>
        </div>
      </div>
    </div>
  );
}

/**
 * Outline pill shared by Follow, the labelled messenger button and the circular
 * icon actions. Hover/active states use Slate's own secondary-button tokens, so
 * these behave like production's buttons rather than sitting inert.
 */
const actionPillStyle = tsv({
  base: [
    "bg-fs-action-surface-inverse border-fs-action-border-subdued text-fs-action-text-default type-fs-paragraph",
    "rounded-full border px-4 py-2 transition-colors",
    "hover:bg-fs-action-surface-inverse-hover hover:border-fs-action-border-hover hover:text-(--slate-color-action-text-inverse-hover)",
    "active:bg-fs-action-surface-inverse-active active:border-fs-action-border-active active:text-(--slate-color-action-text-inverse-active)",
  ],
});

function MetaDivider() {
  return (
    <span className="type-fs-paragraph-medium text-fs-text-subdued mx-1">·</span>
  );
}

function IconCircleButton({
  ariaLabel,
  children,
  onClick,
}: {
  ariaLabel: string;
  children: React.ReactNode;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      onClick={onClick}
      className={cn(
        actionPillStyle.cls(),
        "flex h-10 w-10 items-center justify-center px-0"
      )}
    >
      {children}
    </button>
  );
}

function ShieldIcon() {
  return (
    <svg
      className="h-3 w-4"
      viewBox="0 0 12 16"
      fill="none"
      aria-hidden="true"
      style={{ position: "relative", top: "0.65px" }}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M9.577 11.57a5.43 5.43 0 0 0 1.645-3.888V.254H0v7.428c0 1.457.584 2.859 1.64 3.888.759.741 1.71 1.25 2.737 1.477-1.194.362-2.132 1.236-2.507 2.34h7.481c-.375-1.1-1.317-1.977-2.511-2.34a5.65 5.65 0 0 0 2.737-1.477"
        fill="#333333"
      />
      <path
        d="m5.702 2.723.675 1.772c.02.05.071.075.12.06l1.757-.559c.085-.026.158.068.114.148l-.916 1.652a.105.105 0 0 0 .029.135l1.516 1.076c.074.052.048.17-.04.184L7.14 7.48a.1.1 0 0 0-.083.108l.134 1.9c.007.091-.099.144-.164.081l-1.35-1.293a.095.095 0 0 0-.132 0l-1.35 1.293c-.065.063-.17.01-.164-.081l.134-1.9a.1.1 0 0 0-.083-.108l-1.817-.288c-.087-.014-.113-.132-.04-.184L3.74 5.931a.103.103 0 0 0 .029-.135l-.915-1.652c-.044-.08.029-.174.114-.147l1.757.558a.097.097 0 0 0 .12-.06l.674-1.771a.097.097 0 0 1 .182-.001"
        fill="#fff"
      />
    </svg>
  );
}

function StarIcon() {
  return (
    <svg
      className="h-3.5 w-3.5"
      viewBox="0 0 24 24"
      fill="#333333"
      aria-hidden="true"
    >
      <path d="m12.847 1.87 2.714 5.5a.94.94 0 0 0 .708.515l6.072.884a.94.94 0 0 1 .514 1.603l-4.396 4.286a.94.94 0 0 0-.269.831l1.037 6.055a.94.94 0 0 1-1.371.99l-5.415-2.848a.94.94 0 0 0-.875 0l-5.429 2.856a.94.94 0 0 1-1.371-.99l1.037-6.054a.94.94 0 0 0-.27-.832l-4.388-4.289a.939.939 0 0 1 .514-1.603l6.08-.89a.94.94 0 0 0 .707-.514l2.714-5.5a.94.94 0 0 1 1.687 0" />
    </svg>
  );
}

function DefaultMessageIcon() {
  return (
    <svg
      className="h-5 w-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M11.997.923A11.07 11.07 0 0 0 2.239 6.77a11.08 11.08 0 0 0 .541 11.365L.923 23.077l6.218-1.125a11.072 11.072 0 0 0 15.652-7.46A11.08 11.08 0 0 0 16.81 2.022 11.1 11.1 0 0 0 11.997.923Z" />
    </svg>
  );
}

function MoreIcon() {
  return (
    <svg
      className="h-5 w-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      aria-hidden="true"
    >
      <circle cx="12" cy="3.5" r="2.5" />
      <circle cx="12" cy="12" r="2.5" />
      <circle cx="12" cy="20.5" r="2.5" />
    </svg>
  );
}
